import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";

import FilterCard from "components/_refactor/filter-card";
import StructureFields from "components/_refactor/fields/structure";
import InputText from "components/_refactor/text/input";
import BigNumberCard from "components/_refactor/card/big-number";
import { IsMobile } from "utils/is-mobile";

import BarChartIcon from "@mui/icons-material/BarChart";
import DownloadIcon from "@mui/icons-material/Download";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useVisionLogin } from "hooks/useVisionLogin";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import useLogs from "hooks/logs";
import LoadingData from "components/Loading/LoadingData";
import MaskAndValid from "utils/maskAndValid";
import verifyProfilesVision from "utils/verifyProfilesVision";

import {
  NOTIFY_ALL_IN_SCOPE_KEY,
  buildOverdueRowsForDisplay,
  buildTitlesTrackingReportPath,
  buildUpcomingRowsForDisplay,
  fetchOpenInvoicesDashboard,
  formatDisplayDate,
  formatSalesTotalLabelFromApiValue,
  hasBoletosDashboardSearchCriteria,
  getInvoiceDueMoment,
  getInvoiceRowActionKey,
  getOverdueDayCount,
  normalizeInvoiceRows,
  parseRequestError,
  postInvoiceClientNotifications,
  resolveRepresentativeIdFromSession,
} from "./home-open-boletos-data";

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: theme.spacing(1),
  },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  tableHeadCell: {
    fontWeight: 600,
    color: theme.palette.primary.dark,
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: "nowrap",
  },
  tableCell: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    verticalAlign: "middle",
  },
  actionWrap: {
    display: "inline-flex",
    gap: theme.spacing(0.75),
  },
  actionIconBtn: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    padding: theme.spacing(0.75),
  },
  filterCardWrap: {
    marginTop: theme.spacing(1),
  },
  emptyStateMessage: {
    padding: theme.spacing(3, 2),
    textAlign: "center",
  },
  cardHeaderTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    width: "100%",
  },
}));

function formatBrlFromNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "R$ 0,00";
  return `R$ ${MaskAndValid.maskPrice(n)}`;
}

function isRepresentativePortfolioReady(portfolioId) {
  return portfolioId != null && String(portfolioId).trim() !== "";
}

const BOLETOS_SEARCH_PROMPT_MESSAGE =
  "Utilize os filtros acima (grupo econômico, razão social ou CNPJ) e clique em Pesquisar para visualizar os boletos.";

const BOLETOS_NOT_FOUND_MESSAGE = "Boletos não encontrados.";

const FINANCIAL_SUMMARY_CARDS = [
  {
    title: "Acumulado de Vendas Faturadas",
    legend: "Total de vendas faturadas no ano corrente.",
  },
  {
    title: "Vendas do Mês Faturadas",
    legend: "Total de vendas faturadas no mês corrente.",
  },
  {
    title: "Valor em Atraso",
    legend:
      "Soma do valor dos títulos em aberto vencidos (mesmo filtro da lista; todos os registros).",
  },
  {
    title: "A vencer",
    legend:
      "Soma do valor dos títulos em aberto a vencer nos próximos 30 dias (mesmo filtro da lista; todos os registros).",
  },
];

function FinancialResumoSection({
  overdueAmount,
  upcomingAmount,
  isTitlesBlockLoading,
  yearToDateSalesLabel,
  monthSalesLabel,
  hasSearched,
  isInitialLoading,
}) {
  const classes = useStyles();
  const isMobile = IsMobile();
  const gridItemSm = isMobile ? 12 : 3;

  const overdueAmountLabel = formatBrlFromNumber(overdueAmount);
  const upcomingAmountLabel = formatBrlFromNumber(upcomingAmount);
  const isAnyCardLoading = isTitlesBlockLoading || isInitialLoading;

  const cardValues = [
    yearToDateSalesLabel,
    monthSalesLabel,
    overdueAmountLabel,
    upcomingAmountLabel,
  ];

  const title = (
    <span className={classes.sectionTitleRow}>
      <BarChartIcon color="primary" fontSize="small" />
      Resumo financeiro
    </span>
  );

  return (
    <Card className={classes.card} elevation={3}>
      <CardHeader
        title={title}
        titleTypographyProps={{
          component: Box,
          variant: "h3",
          style: { marginBottom: 0 },
        }}
      />
      <CardContent>
        {!hasSearched && !isAnyCardLoading && !isInitialLoading ? (
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.emptyStateMessage}
          >
            {BOLETOS_SEARCH_PROMPT_MESSAGE}
          </Typography>
        ) : null}
        <Grid container spacing={2}>
          {FINANCIAL_SUMMARY_CARDS.map((card, index) => (
            <Grid item xs={gridItemSm} key={card.title}>
              <BigNumberCard
                title={card.title}
                value={cardValues[index]}
                loading={isAnyCardLoading}
                legend={card.legend}
                onClick={card.onClick}
              />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}

FinancialResumoSection.propTypes = {
  overdueAmount: PropTypes.number.isRequired,
  upcomingAmount: PropTypes.number.isRequired,
  isTitlesBlockLoading: PropTypes.bool.isRequired,
  yearToDateSalesLabel: PropTypes.string.isRequired,
  monthSalesLabel: PropTypes.string.isRequired,
  hasSearched: PropTypes.bool.isRequired,
  isInitialLoading: PropTypes.bool.isRequired,
};

function BoletoTitlesTable({
  classes,
  rows,
  showOverdueDaysColumn,
  repPortfolioId,
  notifyInFlightKey,
  onDownloadRow,
  onNotifyRow,
  hasSearched,
  emptyMessage,
  isInitialLoading,
}) {
  const portfolioReady = isRepresentativePortfolioReady(repPortfolioId);
  const notifyDisabledByPermission = !portfolioReady;

  if (isInitialLoading && !hasSearched) {
    return (
      <LoadingData
        searching
        label="Carregando boletos e resumo financeiro..."
      />
    );
  }

  if (!hasSearched) {
    return (
      <Typography
        variant="body2"
        color="textSecondary"
        className={classes.emptyStateMessage}
      >
        {BOLETOS_SEARCH_PROMPT_MESSAGE}
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeadCell}>CNPJ</TableCell>
            <TableCell className={classes.tableHeadCell}>
              Razão Social
            </TableCell>
            <TableCell className={classes.tableHeadCell}>Nr. Título</TableCell>
            <TableCell className={classes.tableHeadCell} align="center">
              Vencimento
            </TableCell>
            <TableCell className={classes.tableHeadCell} align="right">
              Valor
            </TableCell>
            {showOverdueDaysColumn ? (
              <TableCell className={classes.tableHeadCell} align="center">
                Dias em atraso
              </TableCell>
            ) : null}
            <TableCell className={classes.tableHeadCell} align="center" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.length ? (
            rows.map((row, rowIndex) => {
              const dueMoment = getInvoiceDueMoment(row);
              const rowKey = getInvoiceRowActionKey(row, rowIndex);
              const isRowNotifyLoading =
                notifyInFlightKey === NOTIFY_ALL_IN_SCOPE_KEY ||
                notifyInFlightKey === rowKey;
              const rowBlockedNotify =
                row?.PodeNotificarCliente === false ||
                row?.podeNotificarCliente === false;
              const rowBlockReason =
                row?.MotivoBloqueioNotificacaoCliente ??
                row?.motivoBloqueioNotificacaoCliente ??
                "";
              const rowNotifyTooltip = !portfolioReady
                ? "Aguarde o carregamento da carteira."
                : rowBlockedNotify && rowBlockReason
                  ? rowBlockReason
                  : rowBlockedNotify
                    ? "Notificação indisponível no momento."
                    : "Notificar loja";

              return (
                <TableRow key={rowKey}>
                  <TableCell className={classes.tableCell}>
                    {row.Cnpj ? MaskAndValid.maskCPNJCPF(row.Cnpj) : ""}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {row.RazaoSocial || ""}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {row.NumeroTitulo ?? ""}
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    {dueMoment
                      ? formatDisplayDate(dueMoment.format("YYYY-MM-DD"))
                      : ""}
                  </TableCell>
                  <TableCell className={classes.tableCell} align="right">
                    {row.Valor != null
                      ? `R$${MaskAndValid.maskPrice(row.Valor)}`
                      : ""}
                  </TableCell>
                  {showOverdueDaysColumn ? (
                    <TableCell className={classes.tableCell} align="center">
                      {getOverdueDayCount(row)}
                    </TableCell>
                  ) : null}
                  <TableCell className={classes.tableCell} align="center">
                    <span className={classes.actionWrap}>
                      <Tooltip title={rowNotifyTooltip}>
                        <span>
                          <IconButton
                            type="button"
                            size="small"
                            color="primary"
                            className={classes.actionIconBtn}
                            disabled={
                              notifyDisabledByPermission ||
                              notifyInFlightKey != null ||
                              rowBlockedNotify
                            }
                            onClick={() => {
                              void onNotifyRow(row, rowIndex);
                            }}
                            aria-label={
                              isRowNotifyLoading
                                ? "Enviando lembrete"
                                : "Enviar lembrete"
                            }
                          >
                            {isRowNotifyLoading ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <NotificationsIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Baixar boleto">
                        <IconButton
                          type="button"
                          size="small"
                          color="primary"
                          className={classes.actionIconBtn}
                          onClick={() => onDownloadRow(row)}
                          aria-label="Baixar boleto"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </span>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={showOverdueDaysColumn ? 7 : 6}
                className={classes.tableCell}
                align="center"
              >
                <Typography variant="body2" color="textSecondary">
                  {emptyMessage || BOLETOS_NOT_FOUND_MESSAGE}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function BoletoSection({
  variant,
  rows,
  loading,
  hasSearched,
  isInitialLoading,
  committedFilters,
  repPortfolioId = null,
  representativeIdForTitlesReport = null,
  cnpjCommitted = "",
  totalTitulosNoEscopo = 0,
}) {
  const classes = useStyles();
  const history = useHistory();
  const snackbar = useSnackbar();
  const { setLocalStorageActions } = useLogs();
  const [notifyInFlightKey, setNotifyInFlightKey] = useState(undefined);

  const isOverdueSection = variant === "overdue";
  const situationType = isOverdueSection ? "VENCIDOS" : "AVENCER";
  const representativeIdForNotify = isRepresentativePortfolioReady(
    repPortfolioId,
  )
    ? repPortfolioId
    : null;

  const handleDownloadBoleto = (row) => {
    const downloadUrl =
      row?.UrlDownloadBoleto != null
        ? String(row.UrlDownloadBoleto).trim()
        : "";
    if (!downloadUrl) {
      snackbar.showMessage(
        "Este boleto ainda não está disponível para download.",
        { severity: "info" },
      );
      return;
    }
    window.open(downloadUrl, "_blank");
    setLocalStorageActions({
      nomeTela: "Home — Boletos em aberto",
      idMenu: "",
      acao: "Download do Boleto",
    });
  };

  const sendNotifications = async (
    destinations,
    accessLogActionLabel,
    options = {},
  ) => {
    const { notifyAllInScope = false } = options;

    if (!isRepresentativePortfolioReady(repPortfolioId)) {
      snackbar.showMessage(
        "Carteira do representante ainda não carregada. Tente novamente em instantes.",
        { severity: "warning" },
      );
      return;
    }

    if (!notifyAllInScope && (!destinations || destinations.length === 0)) {
      snackbar.showMessage("Não há títulos para notificar.", {
        severity: "info",
      });
      return;
    }

    try {
      const result = await postInvoiceClientNotifications({
        situationType,
        isFranquiado: false,
        representativeId: representativeIdForNotify,
        committedFilters,
        committedCnpj: cnpjCommitted,
        destinations: notifyAllInScope ? [] : destinations,
        notifyAllInScope,
      });

      if (result?.ProcessamentoAssincrono || result?.processamentoAssincrono) {
        snackbar.showMessage(
          `Notificações em massa enfileiradas para processamento assíncrono.`,
          {
            severity: "success",
          },
        );
      } else {
        snackbar.showMessage(`Cliente notificado.`, {
          severity: "success",
        });
      }

      setLocalStorageActions({
        nomeTela: "Home — Boletos em aberto",
        idMenu: "",
        acao: accessLogActionLabel,
      });
    } catch (error) {
      snackbar.showMessage(
        parseRequestError(error, "Erro ao enviar notificações."),
        { severity: "error" },
      );
    }
  };

  const handleNotifyRow = async (row, rowIndex) => {
    const storePersonId = row?.IdPessoaLojista ?? row?.idPessoaLojista;
    const invoiceNumber = row?.NumeroTitulo ?? row?.numeroTitulo;
    const storeIdNumeric = Number(storePersonId);
    if (!Number.isFinite(storeIdNumeric) || storeIdNumeric <= 0) {
      snackbar.showMessage("Dados da loja inválidos para notificação.", {
        severity: "warning",
      });
      return;
    }
    const rowKey = getInvoiceRowActionKey(row, rowIndex);
    setNotifyInFlightKey(rowKey);
    try {
      await sendNotifications(
        [
          {
            idPessoaLojista: storeIdNumeric,
            numerosTitulo: [String(invoiceNumber).trim()],
          },
        ],
        "Notificação de boleto (linha)",
      );
    } finally {
      setNotifyInFlightKey(undefined);
    }
  };

  const handleNotifyAllInFilterScope = async () => {
    setNotifyInFlightKey(NOTIFY_ALL_IN_SCOPE_KEY);
    try {
      await sendNotifications([], "Notificação de boletos em massa", {
        notifyAllInScope: true,
      });
    } finally {
      setNotifyInFlightKey(undefined);
    }
  };

  const handleOpenTitlesReport = () => {
    const situationPreset = isOverdueSection ? "VENCIDOS" : "AVENCER";
    history.push(
      buildTitlesTrackingReportPath(situationPreset, committedFilters, {
        representativeId: representativeIdForTitlesReport,
      }),
    );
  };

  const portfolioReady = isRepresentativePortfolioReady(repPortfolioId);
  const bulkNotifyTooltip = !portfolioReady
    ? "Aguarde o carregamento da carteira."
    : isOverdueSection
      ? "Notificar todas as lojas com boletos em atraso no escopo dos filtros (além das linhas exibidas)"
      : "Notificar todas as lojas com boletos a vencer no escopo dos filtros (além das linhas exibidas)";

  const cardTitle = (
    <Box className={classes.cardHeaderTitleRow}>
      <span className={classes.sectionTitleRow}>
        {isOverdueSection ? (
          <>
            <WarningAmberIcon color="primary" fontSize="small" />
            Clientes com situação em atraso
          </>
        ) : (
          <>
            <ScheduleIcon color="primary" fontSize="small" />
            Próximos vencimentos
          </>
        )}
      </span>
      <Box>
        <Button color="primary" size="small" onClick={handleOpenTitlesReport}>
          Ver mais
        </Button>
        <Tooltip title={bulkNotifyTooltip}>
          <span>
            <IconButton
              type="button"
              color="primary"
              size="small"
              aria-label={
                notifyInFlightKey === NOTIFY_ALL_IN_SCOPE_KEY
                  ? "Enviando notificações em massa"
                  : "Notificar todas as lojas no escopo dos filtros"
              }
              disabled={
                !portfolioReady ||
                totalTitulosNoEscopo <= 0 ||
                notifyInFlightKey != null
              }
              className={classes.actionIconBtn}
              onClick={() => {
                void handleNotifyAllInFilterScope();
              }}
            >
              {notifyInFlightKey === NOTIFY_ALL_IN_SCOPE_KEY ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <NotificationsActiveIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Card className={classes.card} elevation={3}>
      <CardHeader
        title={cardTitle}
        titleTypographyProps={{
          component: Box,
          variant: "h3",
          style: { marginBottom: 0 },
        }}
      />
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <LoadingData
              searching={loading || (isInitialLoading && !hasSearched)}
              label="Carregando boletos..."
            />
          </Grid>
          {!loading && !(isInitialLoading && !hasSearched) && (
            <Grid item xs={12}>
              <BoletoTitlesTable
                classes={classes}
                rows={rows}
                showOverdueDaysColumn={isOverdueSection}
                repPortfolioId={repPortfolioId}
                notifyInFlightKey={notifyInFlightKey}
                onDownloadRow={handleDownloadBoleto}
                onNotifyRow={handleNotifyRow}
                hasSearched={hasSearched}
                emptyMessage={BOLETOS_NOT_FOUND_MESSAGE}
                isInitialLoading={isInitialLoading}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

export default function HomeOpenBoletosSections() {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { userId } = useVisionLogin();
  const [filtersDraft, setFiltersDraft] = useState({});
  const [cnpjDraft, setCnpjDraft] = useState("");
  const [filtersCommitted, setFiltersCommitted] = useState({});
  const [cnpjCommitted, setCnpjCommitted] = useState("");
  const [clearFilters, setClearFilters] = useState(false);
  const [dashboardPayload, setDashboardPayload] = useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [representativePortfolioId, setRepresentativePortfolioId] =
    useState(null);

  const filtersRef = useRef(filtersCommitted);
  const cnpjRef = useRef(cnpjCommitted);
  const initialLoadTriggeredRef = useRef(false);
  filtersRef.current = filtersCommitted;
  cnpjRef.current = cnpjCommitted;

  const showSection = verifyProfilesVision([
    "REPRESENTANTE",
    "REPRESENTANTE_ADM",
  ]);

  const loadDashboardData = async (structureFilters, cnpj) => {
    const committedFilters = structureFilters ?? filtersRef.current ?? {};
    const committedCnpj = cnpj ?? cnpjRef.current ?? "";

    if (!showSection || !userId || representativePortfolioId == null) {
      setIsDashboardLoading(false);
      return null;
    }

    setIsDashboardLoading(true);
    try {
      const data = await fetchOpenInvoicesDashboard({
        isFranquiado: false,
        structureFilters: committedFilters,
        cnpj: committedCnpj,
        representativeId: representativePortfolioId,
      });
      setDashboardPayload(data);
      return data;
    } catch (error) {
      setDashboardPayload(null);
      snackbar.showMessage(
        parseRequestError(error, "Erro ao carregar boletos."),
        { severity: "error" },
      );
      return null;
    } finally {
      setIsDashboardLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!hasBoletosDashboardSearchCriteria(filtersDraft, cnpjDraft)) {
      snackbar.showMessage(
        "Informe grupo econômico, razão social (loja) ou CNPJ (mín. 11 dígitos) para pesquisar.",
        { severity: "warning" },
      );
      return;
    }
    if (representativePortfolioId == null) {
      snackbar.showMessage(
        "Aguarde o carregamento da carteira do representante para pesquisar.",
        { severity: "warning" },
      );
      return;
    }

    const nextFilters = { ...filtersDraft };
    const nextCnpj = cnpjDraft;
    filtersRef.current = nextFilters;
    cnpjRef.current = nextCnpj;
    setFiltersCommitted(nextFilters);
    setCnpjCommitted(nextCnpj);
    setHasSearched(true);
    await loadDashboardData(nextFilters, nextCnpj);
  };

  const handleClearFilters = async () => {
    setClearFilters(true);
    setCnpjDraft("");
    setFiltersDraft({});
    const emptyFilters = {};
    const emptyCnpj = "";
    filtersRef.current = emptyFilters;
    cnpjRef.current = emptyCnpj;
    setFiltersCommitted(emptyFilters);
    setCnpjCommitted(emptyCnpj);
    setHasSearched(true);
    setTimeout(() => setClearFilters(false), 400);
    if (representativePortfolioId != null) {
      await loadDashboardData(emptyFilters, emptyCnpj);
    } else {
      setDashboardPayload(null);
      setHasSearched(false);
    }
  };

  useEffect(() => {
    if (!showSection) {
      setRepresentativePortfolioId(null);
      return undefined;
    }
    let cancelled = false;
    const loadRepresentativeId = async () => {
      const id = await resolveRepresentativeIdFromSession();
      if (cancelled) return;
      setRepresentativePortfolioId(id);
      if (id == null) {
        setIsInitialLoading(false);
        snackbar.showMessage(
          "Não foi possível identificar a carteira do representante.",
          { severity: "warning" },
        );
      }
    };
    void loadRepresentativeId();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection]);

  useEffect(() => {
    if (!showSection || !userId || representativePortfolioId == null) {
      return undefined;
    }
    if (initialLoadTriggeredRef.current) return undefined;
    initialLoadTriggeredRef.current = true;

    let cancelled = false;
    const runInitialDashboardLoad = async () => {
      setHasSearched(true);
      setIsInitialLoading(true);
      filtersRef.current = {};
      cnpjRef.current = "";
      setFiltersCommitted({});
      setCnpjCommitted("");
      await loadDashboardData({}, "");
      if (!cancelled) setIsInitialLoading(false);
    };
    void runInitialDashboardLoad();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSection, userId, representativePortfolioId]);

  if (!showSection) return null;

  const overdueTitlesRaw =
    dashboardPayload?.TitulosVencidos ??
    dashboardPayload?.titulosVencidos ??
    [];
  const upcomingTitlesRaw =
    dashboardPayload?.TitulosAVencer ?? dashboardPayload?.titulosAVencer ?? [];
  const titlesSummary =
    dashboardPayload?.ResumoTitulos ?? dashboardPayload?.resumoTitulos ?? {};
  const overdueTotalAmount = Number(
    titlesSummary.TotalValorVencidos ?? titlesSummary.totalValorVencidos ?? 0,
  );
  const upcomingTotalAmount = Number(
    titlesSummary.TotalValorAVencer ?? titlesSummary.totalValorAVencer ?? 0,
  );
  const overdueRecordTotal = Number(
    titlesSummary.TotalRegistrosVencidos ??
      titlesSummary.totalRegistrosVencidos ??
      0,
  );
  const upcomingRecordTotal = Number(
    titlesSummary.TotalRegistrosAVencer ??
      titlesSummary.totalRegistrosAVencer ??
      0,
  );
  const overdueRows = buildOverdueRowsForDisplay(
    normalizeInvoiceRows(overdueTitlesRaw),
  );
  const upcomingRows = buildUpcomingRowsForDisplay(
    normalizeInvoiceRows(upcomingTitlesRaw),
  );

  const canSearch =
    hasBoletosDashboardSearchCriteria(filtersDraft, cnpjDraft) &&
    representativePortfolioId != null;
  const isSearchDisabled = isDashboardLoading || isInitialLoading || !canSearch;

  return (
    <>
      {isInitialLoading && representativePortfolioId == null ? (
        <Grid item xs={12}>
          <LoadingData
            searching
            label="Carregando boletos e resumo financeiro..."
          />
        </Grid>
      ) : null}
      <Grid item xs={12} className={classes.filterCardWrap}>
        <FilterCard
          title="Boletos e notificações"
          handleSearch={handleSearch}
          searching={isDashboardLoading}
          disabled={isSearchDisabled}
          handleClearFilters={handleClearFilters}
        >
          <StructureFields
            clearFilters={clearFilters}
            onStructureFieldsChange={setFiltersDraft}
            disableAll={isDashboardLoading || isInitialLoading}
            onStructureFieldsLoad={() => {}}
            defaultBrand={{
              IdMarca: "TODAS",
              Nome: "Todas",
            }}
            hideFields={["channel", "representative", "brand"]}
            fieldsOrder={["economicGroup", "store"]}
            representativePortfolioId={representativePortfolioId}
          />
          <Grid item xs={12} md={6} lg={4}>
            <InputText
              label="CNPJ"
              value={cnpjDraft}
              onChange={(e) => setCnpjDraft(e.target.value)}
              disable={isDashboardLoading || isInitialLoading}
            />
          </Grid>
        </FilterCard>
      </Grid>
      <Grid item xs={12}>
        <FinancialResumoSection
          overdueAmount={
            Number.isFinite(overdueTotalAmount) ? overdueTotalAmount : 0
          }
          upcomingAmount={
            Number.isFinite(upcomingTotalAmount) ? upcomingTotalAmount : 0
          }
          isTitlesBlockLoading={isDashboardLoading}
          hasSearched={hasSearched}
          isInitialLoading={isInitialLoading}
          yearToDateSalesLabel={formatSalesTotalLabelFromApiValue(
            titlesSummary.TotalVendasAnoCorrente ??
              titlesSummary.totalVendasAnoCorrente,
          )}
          monthSalesLabel={formatSalesTotalLabelFromApiValue(
            titlesSummary.TotalVendasMesCorrente ??
              titlesSummary.totalVendasMesCorrente,
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <BoletoSection
          variant="overdue"
          rows={overdueRows}
          loading={isDashboardLoading}
          hasSearched={hasSearched}
          isInitialLoading={isInitialLoading}
          committedFilters={filtersCommitted}
          repPortfolioId={representativePortfolioId}
          representativeIdForTitlesReport={representativePortfolioId}
          cnpjCommitted={cnpjCommitted}
          totalTitulosNoEscopo={
            Number.isFinite(overdueRecordTotal) ? overdueRecordTotal : 0
          }
        />
      </Grid>
      <Grid item xs={12}>
        <BoletoSection
          variant="upcoming"
          rows={upcomingRows}
          loading={isDashboardLoading}
          hasSearched={hasSearched}
          isInitialLoading={isInitialLoading}
          committedFilters={filtersCommitted}
          repPortfolioId={representativePortfolioId}
          representativeIdForTitlesReport={representativePortfolioId}
          cnpjCommitted={cnpjCommitted}
          totalTitulosNoEscopo={
            Number.isFinite(upcomingRecordTotal) ? upcomingRecordTotal : 0
          }
        />
      </Grid>
    </>
  );
}
